import React from 'react';

const LandingPage = ({ onLogin }) => {
  return (
    <div className="landing-page bg-white text-gray-900 antialiased min-h-screen">
      <nav className="container mx-auto px-6 py-6 flex justify-between items-center">
        <div className="text-2xl font-bold tracking-tighter">Kanto</div>
        <div className="hidden md:flex space-x-8">
          <a href="#features" className="text-gray-600 hover:text-black">Features</a>
          <a href="#pricing" className="text-gray-600 hover:text-black">Pricing</a>
        </div>
        <button
          onClick={onLogin}
          className="bg-gray-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-gray-700 transition"
        >
          Login
        </button>
      </nav>

      <header className="container mx-auto px-6 mt-12 mb-24 md:mt-20 md:mb-32">
        <div className="flex flex-col md:flex-row items-center">
          <div className="md:w-1/2 md:pr-12">
            <h1 className="text-5xl md:text-6xl font-extrabold leading-tight mb-6">
              The only list that syncs with your <span className="text-blue-600">whole house.</span>
            </h1>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              AI-powered, offline-capable, and plays nice with Siri & Alexa. Stop fighting with your smart home ecosystem.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={onLogin}
                className="bg-blue-600 text-white text-center px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition shadow-lg shadow-blue-600/30"
              >
                Try Free for 14 Days
              </button>
              <a
                href="#how-it-works"
                className="border border-gray-300 text-center px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                See how it works
              </a>
            </div>
            <p className="mt-4 text-sm text-gray-400">No credit card required for trial.</p>
          </div>

          <div className="md:w-1/2 mt-12 md:mt-0 relative">
            <div className="relative bg-gray-100 rounded-2xl p-8 border border-gray-200 shadow-2xl">
              <img
                src="https://placehold.co/600x400/EEE/31343C?text=Devices+Syncing+Graphic"
                alt="App Interface"
                className="rounded-xl shadow-sm w-full"
              />

              <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-lg shadow-xl border border-gray-100 flex items-center gap-3">
                <div className="bg-green-100 text-green-600 p-2 rounded-full">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Added via Alexa</p>
                  <p className="font-bold text-sm">"Milk" synced to iPhone</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="bg-gray-900 text-white py-24">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4">Why do standard list apps feel broken?</h2>
            <p className="text-gray-400">Because they weren't built for the mixed-device reality of modern families.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 text-center">
            <div className="p-6">
              <div className="bg-gray-800 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 text-red-400">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">The Ecosystem Wall</h3>
              <p className="text-gray-400">Apple Reminders hates Android. Google Keep is clunky on Alexa. You're trapped.</p>
            </div>
            <div className="p-6">
              <div className="bg-gray-800 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 text-red-400">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">The "No Service" Zone</h3>
              <p className="text-gray-400">Try checking your list in the back of a Costco. Most apps freeze. Ours doesn't.</p>
            </div>
            <div className="p-6">
              <div className="bg-gray-800 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 text-red-400">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Sorting Fatigue</h3>
              <p className="text-gray-400">You dictate 20 random items, and you get a jumbled mess. We auto-sort it.</p>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="container mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold">Your Household Operating System</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="border border-gray-200 p-8 rounded-2xl hover:shadow-xl transition duration-300">
            <div className="text-blue-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">The "Switzerland" of Lists</h3>
            <p className="text-gray-600">"Alexa, add milk." "Hey Siri, add bread." It doesn't matter. Kanto catches it all and puts it in one place.</p>
          </div>

          <div className="border border-gray-200 p-8 rounded-2xl hover:shadow-xl transition duration-300">
            <div className="text-blue-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">AI That Organizes</h3>
            <p className="text-gray-600">Dictate "Ingredients for lasagna" and watch as Kanto lists pasta, sauce, and ricotta, then groups them by grocery store aisle.</p>
          </div>

          <div className="border border-gray-200 p-8 rounded-2xl hover:shadow-xl transition duration-300">
            <div className="text-blue-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">Cached & Live</h3>
            <p className="text-gray-600">Local-first architecture. Add items in the dead zone. The second you hit a signal, everyone else sees it.</p>
          </div>

          <div className="border border-gray-200 p-8 rounded-2xl hover:shadow-xl transition duration-300">
            <div className="text-blue-600 mb-4">
              <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold mb-2">The "Honey-Do" Link</h3>
            <p className="text-gray-600">Share a list via a simple URL. The recipient doesn't need to download the app to check items off.</p>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-6 text-center">
          <blockquote className="text-xl md:text-2xl italic text-gray-700 font-medium leading-relaxed max-w-3xl mx-auto">
            "My husband has an Android, I have an iPhone, and we have Echos in every room. This is literally the only app that bridges the gap without failing."
          </blockquote>
          <div className="mt-6 font-bold text-gray-900">— Sarah T., Beta User</div>
        </div>
      </section>

      <section id="pricing" className="py-24 container mx-auto px-6">
        <div className="max-w-md mx-auto bg-black text-white rounded-3xl overflow-hidden shadow-2xl relative">
          <div className="h-2 bg-blue-600 w-full"></div>

          <div className="p-12 text-center">
            <h2 className="text-3xl font-bold mb-2">One Plan. No Confusion.</h2>
            <p className="text-gray-400 mb-8">The price of a coffee for a month of organization.</p>

            <div className="text-6xl font-extrabold mb-2">$5<span className="text-xl font-normal text-gray-400">/mo</span></div>

            <ul className="text-left space-y-4 my-8 text-gray-300 mx-auto max-w-xs">
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Unlimited Lists & Items
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Unlimited AI Sorting
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Two-Way Alexa & Siri Sync
              </li>
              <li className="flex items-center gap-3">
                <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Share with Unlimited Guests
              </li>
            </ul>

            <button
              onClick={onLogin}
              className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl hover:bg-blue-700 transition shadow-lg shadow-blue-900/20"
            >
              Get Kanto Now
            </button>
            <p className="mt-4 text-xs text-gray-500">Cancel anytime.</p>
          </div>
        </div>
      </section>

      <footer className="border-t border-gray-200 py-12 text-center text-gray-500 text-sm">
        <p>&copy; 2024 Kanto. All rights reserved.</p>
        <div className="flex justify-center gap-6 mt-4">
          <a href="#" className="hover:text-black">Privacy</a>
          <a href="#" className="hover:text-black">Terms</a>
          <a href="#" className="hover:text-black">Contact</a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
